import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';
import QuoteResults from '../components/QuoteResults';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || '';
const WHATSAPP_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || '').replace(/\D/g, '');

const QuoteView = () => {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/quotes/${quoteId.toUpperCase()}`);
        if (!cancelled) {
          setQuote(data);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.status === 404 ? 'NOT_FOUND' : 'ERROR');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [quoteId]);

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/q/${quoteId?.toUpperCase()}`
    : '';

  const handleWhatsApp = async () => {
    if (!quote?.pricing) return;
    const text = encodeURIComponent(
      `Take a look at this AI installation estimate from Deep Control:\n\n` +
      `• Quoted price: £${quote.pricing.totalPrice.toLocaleString()}\n` +
      (quote.pricing.rangeLow ? `• Range: £${quote.pricing.rangeLow}–£${quote.pricing.rangeHigh}\n` : '') +
      `• Recommended unit: ${quote.pricing.recommendedUnit}\n` +
      `• Room size: ~${quote.pricing.estimatedArea}m²\n\n` +
      `Full quote: ${shareUrl}`
    );
    const base = WHATSAPP_NUMBER ? `https://wa.me/${WHATSAPP_NUMBER}` : 'https://wa.me/';
    window.open(`${base}?text=${text}`, '_blank', 'noopener,noreferrer');
    try {
      await axios.post(`${API}/quotes/${quoteId.toUpperCase()}/event`, { type: 'WHATSAPP_SHARED' });
    } catch (e) { /* non-blocking */ }
  };

  const handleBookSurvey = () => {
    if (CALENDLY_URL) window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    else window.location.href = '/contact';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d1829] via-[#0f1b2d] to-[#081220] py-24 grid place-items-center" data-testid="quote-view-loading">
        <div className="w-16 h-16 border-4 border-[#FF8C00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error === 'NOT_FOUND') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d1829] via-[#0f1b2d] to-[#081220] py-24" data-testid="quote-view-not-found">
        <div className="max-w-xl mx-auto text-center px-6">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#FF8C00]/10 grid place-items-center">
            <AlertCircle size={40} className="text-[#FF8C00]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Quote not found</h1>
          <p className="text-gray-400 mb-8">This shared estimate may have expired or the link is invalid.</p>
          <a href="/ai-estimator" className="inline-block bg-[#FF8C00] hover:bg-[#e67e00] text-white font-bold px-8 py-4 rounded-lg" data-testid="quote-not-found-cta">
            Generate your own AI estimate
          </a>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d1829] via-[#0f1b2d] to-[#081220] py-24 text-center" data-testid="quote-view-error">
        <p className="text-gray-300">Something went wrong loading this quote. Please try again.</p>
      </div>
    );
  }

  const idUpper = quoteId.toUpperCase();
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d1829] via-[#0f1b2d] to-[#081220] py-16 lg:py-24" data-testid="quote-view-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <SEO
          title="Your AI Air Conditioning Quote"
          description="Personalised AI-generated HVAC installation quote from Deep Control."
          path={`/q/${idUpper}`}
          noindex
        />
        <QuoteResults
          pricing={quote.pricing}
          narrative={quote.narrative}
          rooms={quote.rooms || []}
          outdoor={quote.outdoor}
          outdoorImageUrl={quote.outdoor?.hasImage ? `${API}/quotes/${idUpper}/outdoor/image` : null}
          roomImageBaseUrl={(idx, type) => `${API}/quotes/${idUpper}/rooms/${idx}/${type}`}
          mockMode={!!quote.mockMode}
          shareUrl={shareUrl}
          isSharedView
          onWhatsApp={handleWhatsApp}
          onBookSurvey={handleBookSurvey}
          onReset={null}
          onCopyLink={null}
        />
      </div>
    </div>
  );
};

export default QuoteView;
